import { Fragment, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { routeScrollKey, willRestoreScroll } from '@/lib/scrollRestore';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowUp, Award, CalendarDays, CheckCircle2, CircleCheckBig, MapPin, Megaphone, MessageCircle, MessagesSquare, ShieldCheck, Siren, Sparkles } from 'lucide-react';
import { describePremium, feedBuildingCode, feedPost, feedThreadUnread, newerComment, FEED_SCOPE_LABEL, FEED_TYPE_LABEL, FEED_TYPES, feedNewSinceIndex, feedStream, isAboutPinned, pinnedRefs, type FeedItem, type PinnedActivity, type FeedScope, type FeedThread, type FeedType, RealtimeEvents, topics } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { useCanAnywhere } from '@/lib/permissions';
import { Button, buttonClass, EmptyState, HoverBubble, Select, Spinner } from '@/components/ui';
import { entityThreadKey } from '@/features/threads/EntityThread';
import { RelativeTime } from '@/components/time/RelativeTime';
import { DATE_FORMATS, relativeFuture } from '@/lib/format';
import { AnnouncementCard } from '@/features/knowledge/AnnouncementsPage';
import { GiveKudosModal, KudosCard } from '@/features/people/KudosPage';
import { PersonButton } from '@/features/people/PersonDrawer';
import { CommentsPreview, useCommentCount } from '@/features/threads/CommentsPreview';
import { ItemReactions } from '@/components/reactions/ItemReactions';
import { FeedNewDivider, ackThreadLink } from './feedShared';
import { BuildingCode, PostCard, PostLatest, PostThreadFooter, TaskPostFooter } from './FeedPost';
import { useFeedVisit } from '@tradeoutlet/data';
import { EventDetailModal } from '@/features/calendar/EventModals';
import { useOriginNavigate } from '@/lib/origin';
import { realtime } from '@/lib/realtime';
import { useAuth } from '@/lib/auth';
import { cardOpenHandlers } from '@/lib/cardOpen';

const STORE = 'home.feed.filters';
interface Filters { types: FeedType[]; scope: FeedScope }
const load = (): Filters => {
  try {
    const v = JSON.parse(localStorage.getItem(STORE) ?? 'null') as Partial<Filters> | null;
    return { types: (v?.types ?? []).filter(t => (FEED_TYPES as readonly string[]).includes(t)), scope: v?.scope && v.scope in FEED_SCOPE_LABEL ? v.scope : 'all' };
  } catch { return { types: [], scope: 'all' }; }
};
/** t298u: each feed card's wrapper carries its id, so Home restores by card; cached pages are kept this long. */
export const FEED_ANCHOR = 'data-feed-id';
export const FEED_KEEP_MS = 30 * 60_000;
const refresh = (ws: string | null) => queryClient.invalidateQueries({ queryKey: ['home', ws, 'feed'] });

/**
 * t184u: every card's reactions and comments. Comments are the item's own discussion thread (as on combined cards);
 * Comment opens it with the composer focused.
 */
function ItemFooter({ type, id, count, canReply = true, canReact = true }: { type: string; id: string; count?: number; /** t522u */ canReply?: boolean; canReact?: boolean }) {
  const n = useCommentCount(type, id, count);
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="mt-2 flex items-center gap-2" data-testid="item-footer">
        <ItemReactions type={type} id={id} canAdd={canReact} />
        {!open && !(n ?? 0) && canReply && (
          <button type="button" onClick={() => setOpen(true)} data-testid="comment-button"
            className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><MessageCircle className="h-4 w-4" />Comment</button>
        )}
      </div>
      <CommentsPreview type={type} entityId={id} count={count} open={open} hideButton autoFocus={open} />
    </>
  );
}

function AckFeedCard({ item }: { item: Extract<FeedItem, { kind: 'ack' }> }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const a = item.ack;
  const go = async () => {
    setBusy(true);
    try { await api.acknowledge(a.message_id); toast('Acknowledged', 'success'); void refresh(ws); void queryClient.invalidateQueries({ queryKey: ['acks'] }); }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const overdue = a.due_at && Date.parse(a.due_at) < Date.now();
  const nav = useNavigate();
  return (
    <article className="cursor-pointer rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm dark:border-amber-900/60 dark:bg-amber-950/20" data-testid="feed-ack" {...cardOpenHandlers(() => nav(a.link))}>
      <header className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
        <ShieldCheck className="h-4 w-4" aria-hidden />Acknowledgement needed
        {a.due_at && <span className={clsx('ml-auto font-medium normal-case tracking-normal', overdue ? 'text-rose-700 dark:text-rose-300' : 'text-amber-800 dark:text-amber-200')}>
          {overdue ? <>Overdue · was due <RelativeTime iso={a.due_at} /></> : <span title={new Date(a.due_at).toLocaleString('en-GB')}>Due {relativeFuture(a.due_at)}</span>}</span>}
      </header>
      <p className="mt-2 text-[15px] leading-relaxed">{a.preview}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="mr-auto text-xs text-slate-600 dark:text-slate-300"><PersonButton userId={a.author_id} name={a.author_name} className="rounded font-medium hover:underline">{a.author_name}</PersonButton> in {a.conversation_name} · <RelativeTime iso={a.posted_at} /></span>
        <Link to={a.link} className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-white dark:text-slate-200 dark:hover:bg-slate-800">View message</Link>
        <Button size="sm" onClick={() => void go()} disabled={busy}><CheckCircle2 className="h-4 w-4" />Acknowledge</Button>
      </div>
      {/* t184u: the message's own reactions, and comments as replies in its chat thread. */}
      <div className="mt-2 flex cursor-auto items-center gap-2" data-testid="item-footer" data-no-card-open>
        <ItemReactions type="message" id={a.message_id} />
        <Link to={ackThreadLink(a)} data-testid="ack-reply" className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><MessageCircle className="h-4 w-4" />Reply</Link>
      </div>
    </article>
  );
}

/** t145: a shift offered to you - answer it here. */
function ShiftOfferFeedCard({ item }: { item: Extract<FeedItem, { kind: 'shift_offer' }> }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const o = item.offer;
  const tz = (o.shift as unknown as { timezone?: string }).timezone ?? 'Europe/London';
  const start = DateTime.fromISO(String(o.shift.starts_at), { zone: tz });
  const end = DateTime.fromISO(String(o.shift.ends_at), { zone: tz });
  const premium = describePremium({ premium_type: o.shift.premium_type ?? null, premium_value: o.shift.premium_value ?? null });
  const sender = o.created_by;
  const answer = async (action: 'accept' | 'decline') => {
    setBusy(true);
    try {
      const r = await api.wf.respondShiftOffer(ws!, o.id, action);
      toast(r.status === 'assigned' ? "It's yours - added to your shifts" : r.status === 'awaiting_confirmation' ? 'Thanks - your manager will confirm' : 'Declined', 'success');
    } catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(false); void refresh(ws); void queryClient.invalidateQueries({ queryKey: ['wf', ws] }); }
  };
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900" data-testid="feed-shift-offer">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-yellow text-on-accent-strong" aria-hidden>
          <span className="text-center leading-none"><span className="block text-[10px] font-semibold uppercase">{start.toFormat('LLL')}</span><span className="block text-sm font-bold">{start.toFormat('d')}</span></span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-500">
            <PersonButton userId={sender} name={o.sender_name ?? undefined} className="font-medium text-slate-700 hover:underline dark:text-slate-200">{o.sender_name ?? 'A manager'}</PersonButton>
            {' offered you a shift'} · <RelativeTime iso={item.at} />
          </p>
          <p className="mt-0.5 font-semibold">{start.toFormat(DATE_FORMATS.weekday)} · {start.toFormat(DATE_FORMATS.time)}–{end.toFormat(DATE_FORMATS.time)}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-sm text-slate-600 dark:text-slate-300">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{o.shift.location_name}{o.shift.position_name ? ` · ${o.shift.position_name}` : ''}</span>
            {premium && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200" title={o.shift.premium_reason ?? 'Premium pay'}>{premium}</span>}
            {o.expires_at && <span className="text-xs" title={new Date(o.expires_at).toLocaleString('en-GB')}>Expires {relativeFuture(o.expires_at)}</span>}
          </p>
          {o.message && <p className="mt-1 text-sm italic text-slate-600 dark:text-slate-300">“{o.message}”</p>}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Button size="sm" onClick={() => void answer('accept')} disabled={busy} data-testid="feed-offer-accept"><CheckCircle2 className="h-4 w-4" />{o.kind === 'direct' || o.mode === 'first_accept' ? 'Accept' : 'I can do it'}</Button>
            <Button size="sm" variant="ghost" onClick={() => void answer('decline')} disabled={busy} data-testid="feed-offer-decline">Decline</Button>
            <Link to="/workforce/my-shifts?tab=offers" className="ml-auto rounded-md px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">My shifts</Link>
          </div>
        </div>
      </div>
      {/* t184u: a shift offer has its own thread now (questions about the shift go to the sender and rota editors). */}
      <ItemFooter type="shift_offer" id={o.id} />
    </article>
  );
}

/** t164u: unread comments on a pinned post. Shown on its card instead of a discussion card further down. t174u: any card's new comments. */
function PinnedNewComments({ id, activity, type = 'announcement' }: { id: string; activity: PinnedActivity; type?: string }) {
  const ws = useWorkspaceId();
  const names = activity.actors.map(x => x.name);
  const who = names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  const seen = () => { if (ws) void api.markEntityNotificationsRead(ws, type, id).then(() => refresh(ws)).catch(() => undefined); };
  return (
    <HoverBubble bubble={<>From {who} · <RelativeTime iso={activity.latest_at} /></>}>
      <button type="button" onClick={seen} data-testid="pinned-new-comments"
        className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-on-accent">
        <MessagesSquare className="h-3.5 w-3.5" aria-hidden />{activity.count} new comment{activity.count === 1 ? '' : 's'}
      </button>
    </HoverBubble>
  );
}

function AnnouncementFeedCard({ item, activity }: { item: Extract<FeedItem, { kind: 'announcement' }>; activity?: PinnedActivity }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const a = item.announcement;
  // New comments arrived on a pinned post: refresh its comments in place.
  const n = activity?.count ?? 0;
  useEffect(() => {
    if (!ws || !n) return;
    const conv = (queryClient.getQueryData(entityThreadKey(ws, 'announcement', a.id)) as { conversation_id?: string } | undefined)?.conversation_id;
    void queryClient.invalidateQueries({ predicate: q => q.queryKey.includes(a.id) || (Boolean(conv) && q.queryKey.includes(conv)) });
  }, [ws, a.id, n, activity?.latest_at]);
  const ack = async () => {
    try { await api.announcements.ack(ws!, a.id); toast('Acknowledged', 'success'); void refresh(ws); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <div className="relative" data-testid="feed-announcement">
      <AnnouncementCard a={a} media="hero" />
      {a.ack_required && <div className="absolute right-3 top-3"><Button size="sm" onClick={() => void ack()}><CheckCircle2 className="h-4 w-4" />Acknowledge</Button></div>}
      <div className="-mt-2 rounded-b-xl border-x border-b border-slate-200 bg-white px-4 pb-3 pt-1 dark:border-slate-800 dark:bg-slate-900">
        {activity && activity.count > 0 && <div className="pt-2"><PinnedNewComments id={a.id} activity={activity} /></div>}
        <ItemFooter type="announcement" id={a.id} canReply={a.can_reply !== false} canReact={a.can_react !== false} />
      </div>
    </div>
  );
}


/** t174u: a card's merged comments as the new-comments chip. */
/** t358u: only while there are comments you haven't read since your own latest one. */
const threadActivity = (th: FeedThread | undefined): PinnedActivity | undefined =>
  th && th.count > 0 && feedThreadUnread(th) ? { count: th.count, actors: th.actors, latest_at: th.comments[0]?.at ?? '' } : undefined;
function WithNewComments({ th, children }: { th?: FeedThread; children: React.ReactNode }) {
  const a = threadActivity(th);
  return a && th ? <div>{children}<div className="mt-1 flex justify-end"><PinnedNewComments id={th.entity_id} type={th.entity_type} activity={a} /></div></div> : <>{children}</>;
}

const taskIdOf = (link: string) => new URL(link, window.location.origin).searchParams.get('task');

/** A calendar event (t156u): when, where, RSVP, discussion. Opens in the event dialog over Home (t158u). */
function EventFeedCard({ item }: { item: Extract<FeedItem, { kind: 'event' }> }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [open, setOpen] = useState(false);
  const [rsvp, setRsvp] = useState(item.event.my_rsvp);
  const e = item.event;
  const start = DateTime.fromISO(e.starts_local, { zone: e.timezone });
  const end = DateTime.fromISO(e.ends_local, { zone: e.timezone });
  const when = e.all_day ? `${start.toFormat(DATE_FORMATS.weekday)} · All day` : `${start.toFormat(DATE_FORMATS.weekday)} · ${start.toFormat(DATE_FORMATS.time)}–${end.toFormat(DATE_FORMATS.time)}`;
  // t298u: one muted "Latest: Sam commented 2 hours ago" line instead of a new-comments chip; the comments are below.
  const latest = newerComment(item, item.event_at ?? (item.thread ? undefined : item.at));
  const bcode = feedBuildingCode(e.location_name, e.location_code); // t701u
  const answer = async (r: 'accepted' | 'tentative' | 'declined') => {
    const prev = rsvp; setRsvp(r);
    try { await api.rsvpEvent(e.id, r); } catch (err) { setRsvp(prev); toast(errorMessage(err), 'error'); }
  };
  return (
    <article className="cursor-pointer rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900" data-testid="feed-event" {...cardOpenHandlers(() => setOpen(true))}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" aria-hidden>
          <span className="text-center leading-none"><span className="block text-[10px] font-semibold uppercase">{start.toFormat('LLL')}</span><span className="block text-sm font-bold">{start.toFormat('d')}</span></span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-500">
            <PersonButton userId={e.created_by} name={e.created_by_name} className="font-medium text-slate-700 hover:underline dark:text-slate-200">{e.created_by_name}</PersonButton>
            {item.reason === 'invited' ? ' invited you' : ' added an event'}{e.location_name && !bcode ? ` · ${e.location_name}` : !e.location_name && e.team_name ? ` · ${e.team_name}` : ''} · <RelativeTime iso={item.at} />
          </p>
          <button type="button" onClick={() => setOpen(true)} className="mt-0.5 text-left font-semibold hover:underline">{e.title}</button>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-sm text-slate-600 dark:text-slate-300">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{when}</span>
            {e.location_text && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location_text}</span>}
          </p>
          {e.description && <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{e.description}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {rsvp !== null && (['accepted', 'tentative', 'declined'] as const).map(r => (
              <button key={r} type="button" onClick={() => void answer(r)} aria-pressed={rsvp === r} data-testid={`feed-rsvp-${r}`}
                className={clsx('rounded-full border px-2.5 py-0.5 text-xs font-medium', rsvp === r ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink' : 'border-slate-200 hover:border-slate-400 dark:border-slate-700')}>
                {r === 'accepted' ? 'Going' : r === 'tentative' ? 'Maybe' : 'Can’t go'}
              </button>
            ))}
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setOpen(true)}>Open</Button>
          </div>
        </div>
        {bcode && <BuildingCode b={bcode} />}
      </div>
      {latest && <PostLatest l={latest} className="mt-2 pl-[52px]" />}
      <div className="cursor-auto pl-[52px]" data-no-card-open><PostThreadFooter type="calendar_event" id={e.id} /></div>
      {open && <EventDetailModal id={e.id} occurrence={null} onClose={() => { setOpen(false); void refresh(ws); }} onEdit={() => undefined} />}
    </article>
  );
}

function useOpenThread(th: Pick<FeedThread, 'entity_type' | 'entity_id' | 'link'>) {
  const openTask = useUi(s => s.openTask);
  const go = useOriginNavigate();
  const ws = useWorkspaceId();
  const [eventOpen, setEventOpen] = useState(false);
  const open = () => {
    if (ws) void api.markEntityNotificationsRead(ws, th.entity_type, th.entity_id).catch(() => undefined);
    if (th.entity_type === 'calendar_event') return setEventOpen(true);
    const task = taskIdOf(th.link);
    if (th.entity_type === 'task' && task) return openTask(task);
    go(th.link);
  };
  const modal = eventOpen ? <EventDetailModal id={th.entity_id} occurrence={null} onClose={() => setEventOpen(false)} onEdit={() => undefined} /> : null;
  return { open, modal };
}

/** t298u: a discussion as a post about its subject (an event, an issue, a task, kudos), with its latest comments under it. */
function DiscussionFeedCard({ item }: { item: Extract<FeedItem, { kind: 'discussion' }> }) {
  const th = item.thread;
  const { open, modal } = useOpenThread(th);
  const post = feedPost(item)!;
  const task = th.entity_type === 'task';
  return (
    <>
      <PostCard post={post} testId="feed-discussion" entity={`${th.entity_type}:${th.entity_id}`} onOpen={open} icon={<MessagesSquare className="h-4 w-4" />} tone="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
        {task ? <TaskPostFooter taskId={th.entity_id} total={null} th={th} onOpen={open} /> : <PostThreadFooter type={th.entity_type} id={th.entity_id} />}
      </PostCard>
      {modal}
    </>
  );
}

/** t174u: one card per issue, whatever happened to it. t298u: a post about the issue, its latest comments under it. */
function IssueFeedCard({ item }: { item: Extract<FeedItem, { kind: 'issue' }> }) {
  const i = item.issue;
  const ws = useWorkspaceId();
  const go = useOriginNavigate();
  // t362u: closed without a fix is grey like its strip; resolved stays green.
  const tone = i.status === 'resolved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
    : i.status === 'closed' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    : i.severity === 'critical' || i.severity === 'high' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200';
  const open = () => { if (ws) void api.markEntityNotificationsRead(ws, 'incident', i.id).catch(() => undefined); go(`/incidents/${i.id}`); };
  return (
    <PostCard post={feedPost(item)!} testId="feed-issue" entity={`incident:${i.id}`} onOpen={open} icon={<Siren className="h-4 w-4" />} tone={tone}>
      <PostThreadFooter type="incident" id={i.id} count={i.comment_count} />
    </PostCard>
  );
}

/** t174u: a completed task with its comments on one card. t298u: a post about the task. */
function TaskFeedCard({ item }: { item: Extract<FeedItem, { kind: 'task_completed' }> }) {
  const openTask = useUi(s => s.openTask);
  const go = useOriginNavigate();
  const t = item.task;
  const id = taskIdOf(t.href);
  const open = () => (id ? openTask(id) : go(t.href));
  return (
    <PostCard post={feedPost(item)!} testId="feed-task" entity={`task:${t.id}`} onOpen={open} icon={<CircleCheckBig className="h-4 w-4" />} tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
      <TaskPostFooter taskId={t.id} total={t.comment_count ?? null} th={item.thread} onOpen={open} />
    </PostCard>
  );
}

export function FeedCard({ item }: { item: FeedItem }) {
  switch (item.kind) {
    case 'announcement': return <AnnouncementFeedCard item={item} activity={threadActivity(item.thread)} />;
    case 'kudos': case 'birthday': return <WithNewComments th={item.thread}><div data-testid={`feed-${item.kind}`}><KudosCard post={item.kudos} /></div></WithNewComments>;
    case 'ack': return <AckFeedCard item={item} />;
    case 'shift_offer': return <ShiftOfferFeedCard item={item} />;
    case 'event': return <EventFeedCard item={item} />;
    case 'discussion': return <DiscussionFeedCard item={item} />;
    case 'task_completed': return <TaskFeedCard item={item} />;
    case 'issue': return <IssueFeedCard item={item} />;
  }
}

/** Home feed (t131u): curated stream with type + scope filters, pinned posts on top, infinite scroll. */
/**
 * The row above the feed: Give kudos and (for publishers) Announce. t484u: the feed has no free-text post, so this is a
 * plain row with a muted label, not a fake input: no box, no I-beam, nothing focusable but the buttons.
 */
export function FeedShareRow({ onKudos, canPublish }: { onKudos: () => void; canPublish: boolean }) {
  return (
    <div className="mb-3 flex cursor-default select-none items-center gap-2 px-1" data-testid="feed-share-row">
      <span className="mr-auto truncate text-sm text-slate-500 dark:text-slate-400 max-sm:sr-only">Share with the team</span>
      <Button size="sm" variant="secondary" className="whitespace-nowrap max-sm:ml-auto" onClick={onKudos}><Award className="h-4 w-4" />Give kudos</Button>
      {canPublish && <Link to="/announcements?new=1" className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800"><Megaphone className="h-4 w-4" />Announce</Link>}
    </div>
  );
}

/** Feed filter chips; t644u: the selected one is a fill in the org theme colour. */
export const feedChipClass = (on: boolean) => clsx('shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition',
  on ? 'border-transparent bg-accent text-on-accent' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200');
/** t344u/t644u: phones scroll the chips sideways; wider screens wrap them so none is cut off under the scope selector. */
export const FEED_FILTER_ROW = 'scroll-none flex min-w-0 flex-1 gap-1.5 overflow-x-auto sm:flex-wrap sm:overflow-visible';

/**
 * t643u: on a computer, Give kudos and New announcement sit in the Home header (between the greeting and Layout) as two
 * compact buttons instead of the row above the feed. Same actions and permission: New announcement only for publishers.
 */
export function HomeShareButtons() {
  const ws = useWorkspaceId();
  const [giving, setGiving] = useState(false);
  const canPublish = useCanAnywhere('comms.announcement.publish');
  return (
    <div className="flex items-center gap-2" data-testid="home-share-buttons">
      <Button size="sm" variant="secondary" type="button" className="whitespace-nowrap" onClick={() => setGiving(true)}><Award className="h-4 w-4" />Give kudos</Button>
      {canPublish && <Link to="/announcements?new=1" className={buttonClass('secondary', 'sm', 'whitespace-nowrap')} data-testid="home-new-announcement"><Megaphone className="h-4 w-4" />New announcement</Link>}
      {giving && <GiveKudosModal onClose={posted => { setGiving(false); if (posted) void refresh(ws); }} />}
    </div>
  );
}

/** `shareRow`: the Give kudos / Announce row above the feed (phones); on a computer they're in the Home header (t643u). */
export function HomeFeed({ shareRow = true }: { shareRow?: boolean } = {}) {
  const ws = useWorkspaceId();
  const [f, setF] = useState<Filters>(load);
  const [giving, setGiving] = useState(false);
  const canPublish = useCanAnywhere('comms.announcement.publish');
  useEffect(() => { localStorage.setItem(STORE, JSON.stringify(f)); }, [f]);
  // t298u: coming back (Back, or the Home link within 30 minutes) keeps the cached pages exactly as they were - no
  // refetch that could reorder the cards under you. New activity waits behind the "N new posts" pill.
  const loc = useLocation();
  const navType = useNavigationType();
  const [keep] = useState(() => willRestoreScroll(routeScrollKey(loc.pathname, loc.search), navType, FEED_KEEP_MS));
  const q = useInfiniteQuery({
    queryKey: ['home', ws, 'feed', f.types.join(','), f.scope],
    queryFn: ({ pageParam }) => api.home.feed(ws!, { types: f.types, scope: f.scope, before: pageParam ?? undefined, limit: 15 }),
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_before,
    enabled: Boolean(ws),
    // t184u: the loaded pages stay cached while an item is open, so Back lands on the same cards.
    gcTime: FEED_KEEP_MS, staleTime: 60_000, refetchOnMount: !keep, refetchOnWindowFocus: false,
  });
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const io = new IntersectionObserver(es => { if (es[0]?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage(); }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [q]);
  // t156u: "N new posts" without a reload. Check the head of the feed when something may have changed (a notification
  // arrives, the tab comes back) and every minute; new items wait behind the pill so the page never jumps.
  const top = q.data?.pages[0]?.items[0]?.at ?? null;
  const head = useQuery({
    queryKey: ['home', ws, 'feed-head', f.types.join(','), f.scope],
    queryFn: () => api.home.feed(ws!, { types: f.types, scope: f.scope, limit: 15 }),
    enabled: Boolean(ws && q.data), refetchInterval: 60_000, refetchOnWindowFocus: true,
  });
  const { userId } = useAuth();
  useEffect(() => (userId ? realtime.subscribe(topics.user(userId), event => { if (event === RealtimeEvents.Notification) void head.refetch(); }) : undefined), [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Pinned posts: the head check is fresher (new comments land there); nothing about them shows in the stream (t164u).
  const first = head.data ?? q.data?.pages[0];
  const pinned = first?.pinned ?? [];
  const activity = first?.pinned_activity ?? {};
  const refs = pinnedRefs(pinned);
  // t174u: cards are one per item, so a shown card with newer activity (it moves to the top) counts as new too.
  const shownAt = new Map(q.data?.pages[0]?.items.map(i => [i.id, i.at]) ?? []);
  const fresh = top ? (head.data?.items ?? []).filter(i => i.at > top && shownAt.get(i.id) !== i.at && !isAboutPinned(i, refs)).length : 0;
  const showNew = () => { void q.refetch(); document.querySelector('[data-testid="home-feed"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }); };
  const toggle = (t: FeedType) => setF(x => ({ ...x, types: x.types.includes(t) ? x.types.filter(y => y !== t) : [...x.types, t] }));
  const items = feedStream(q.data?.pages ?? [], pinned);
  const since = useFeedVisit(ws);
  const divider = feedNewSinceIndex(items, since);
  const chip = feedChipClass;
  return (
    <section aria-label="Feed" data-testid="home-feed" className="min-w-0">
      {shareRow && <FeedShareRow onKudos={() => setGiving(true)} canPublish={canPublish} />}
      <div className="sticky top-0 z-10 -mx-1 mb-3 flex items-center gap-1.5 bg-slate-50/95 px-1 py-2 backdrop-blur dark:bg-slate-950/95 sm:items-start" role="toolbar" aria-label="Filter the feed" data-testid="feed-filters">
        {/* t344u: phones: one row that scrolls sideways. t644u: wider screens wrap the chips onto a second line, so none
            (Issues, the last, was cut off) hides under the Everywhere selector, which keeps the end of the row. */}
        <div className={FEED_FILTER_ROW} data-testid="feed-filter-row">
          <button type="button" className={chip(!f.types.length)} aria-pressed={!f.types.length} onClick={() => setF(x => ({ ...x, types: [] }))}>All</button>
          {FEED_TYPES.map(t => <button key={t} type="button" className={chip(f.types.includes(t))} aria-pressed={f.types.includes(t)} onClick={() => toggle(t)}>{FEED_TYPE_LABEL[t]}</button>)}
        </div>
        <Select aria-label="Whose news" className="!w-auto shrink-0 !py-1 text-xs" value={f.scope} onChange={e => setF(x => ({ ...x, scope: e.target.value as FeedScope }))} data-testid="feed-scope">
          {(Object.keys(FEED_SCOPE_LABEL) as FeedScope[]).map(s => <option key={s} value={s}>{FEED_SCOPE_LABEL[s]}</option>)}
        </Select>
      </div>
      {fresh > 0 && (
        <div className="sticky top-12 z-20 -mt-1 mb-2 flex justify-center">
          <button type="button" onClick={showNew} data-testid="feed-new-pill"
            className="flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white shadow-lg hover:brightness-110 dark:bg-white dark:text-ink">
            <ArrowUp className="h-3.5 w-3.5" />{fresh} new post{fresh === 1 ? '' : 's'}
          </button>
        </div>
      )}
      {pinned.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="feed-pinned">
          {pinned.map(a => <div key={a.id} {...{ [FEED_ANCHOR]: `pin:${a.id}` }}><AnnouncementFeedCard item={{ kind: 'announcement', id: a.id, at: a.publish_at, announcement: a }} activity={activity[a.id]} /></div>)}
        </div>
      )}
      {q.isLoading ? <Spinner /> : q.error ? <p className="text-sm text-rose-700">{errorMessage(q.error)}</p> : !items.length ? (
        <EmptyState icon={<Sparkles className="h-8 w-8" />} title="Nothing here yet"><p className="text-sm text-slate-500">{f.types.length || f.scope !== 'all' ? 'Nothing matches these filters. Try All or Everywhere.' : 'Kudos, announcements and team news will show up here.'}</p></EmptyState>
      ) : (
        <div className="space-y-3" data-testid="feed-items">{items.map((it, i) => <Fragment key={it.id}>{i === divider && <FeedNewDivider />}<div {...{ [FEED_ANCHOR]: it.id }}><FeedCard item={it} /></div></Fragment>)}</div>
      )}
      <div ref={sentinel} className="h-8" aria-hidden />
      {q.isFetchingNextPage && <Spinner />}
      {!q.hasNextPage && items.length > 0 && <p className="py-4 text-center text-xs text-slate-500">You’re all caught up.</p>}
      {giving && <GiveKudosModal onClose={posted => { setGiving(false); if (posted) void refresh(ws); }} />}
    </section>
  );
}
